import { useMemo, useState } from 'react';
import { Alert } from '../../../components/ui/Alert';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { useImportList } from '../../../hooks/usePilots';
import type { ImportRow, Rep } from '../../../api/pilots';
import { inputClass } from './PeopleTable';

/**
 * Paste a list straight from a sheet: one row per person, with the company on
 * the row. Columns are recognised by their header; the mapping can be corrected
 * before anything is imported.
 */

const FIELDS = ['company', 'domain', 'companyCountry', 'fullName', 'firstName', 'lastName', 'jobTitle', 'linkedin', 'email', 'personCountry', 'rep'] as const;
type Field = (typeof FIELDS)[number];

const FIELD_LABEL: Record<Field, string> = {
  company: 'Company', domain: 'Domain / website', companyCountry: 'Company country', personCountry: 'Person location', fullName: 'Full name', firstName: 'First name', lastName: 'Last name',
  jobTitle: 'Job title', linkedin: 'LinkedIn', email: 'Email', rep: 'Rep',
};

/** Header patterns, checked in this order; the first field that matches wins the column. */
const HEADER_MATCH: [Field, RegExp][] = [
  ['linkedin', /^linkedin( profile| url)?$|^linkedin ?(profile )?url$|^person linkedin$/],
  ['email', /^(work |person )?e-?mail( address)?$/],
  ['firstName', /^first ?name$|^first$|^voornaam$|^prénom$/],
  ['lastName', /^last ?name$|^last$|^surname$|^achternaam$|^nom$/],
  ['fullName', /^(full ?name|name|person|contact|naam|contact name)$/],
  // "Role" alone is often a category (Bid lead), not the person's title, so it is not taken.
  ['jobTitle', /^(job )?title$|^position$|^function$|^functie$|^role title$/],
  ['domain', /^(company )?(domain|website|web ?site|url|site)$/],
  ['company', /^(company|company ?name|account|organi[sz]ation|employer|bedrijf|entreprise|account name)$/],
  ['personCountry', /^(person country|based in|location|person location|city|land)$/],
  ['companyCountry', /^(hq country|company country|search country|country|hq|headquarters)$/],
  ['rep', /^(rep|owner|assignee|assigned( to)?|sdr|ae|sales ?rep|team ?member)$/],
];

function detectDelimiter(header: string): string {
  if (header.includes('\t')) return '\t';
  return (header.match(/;/g)?.length ?? 0) > (header.match(/,/g)?.length ?? 0) ? ';' : ',';
}

/** Minimal CSV/TSV row split with quoted fields. */
function splitRow(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cur += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delimiter) { out.push(cur); cur = ''; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

export function parsePasted(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const delimiter = detectDelimiter(lines[0]!);
  const headers = splitRow(lines[0]!, delimiter);
  const rows = lines.slice(1).map((l) => splitRow(l, delimiter));
  return { headers, rows };
}

export function guessMapping(headers: string[]): Partial<Record<Field, number>> {
  const mapping: Partial<Record<Field, number>> = {};
  headers.forEach((h, i) => {
    const key = h.trim().toLowerCase();
    const hit = HEADER_MATCH.find(([field, re]) => !(field in mapping) && re.test(key));
    if (hit) mapping[hit[0]] = i;
  });
  return mapping;
}

/** A rep column may hold an email, a full name or a first name. */
function repEmailFor(value: string, reps: Rep[]): string | null {
  const v = value.trim().toLowerCase();
  if (!v) return null;
  return reps.find((r) => r.email.toLowerCase() === v)?.email
    ?? reps.find((r) => r.name?.toLowerCase() === v)?.email
    ?? reps.find((r) => r.name?.toLowerCase().split(' ')[0] === v || r.email.split('@')[0]?.toLowerCase() === v)?.email
    ?? null;
}

export function ImportListModal({ bucketId, isOpen, onClose, reps }: { bucketId: string; isOpen: boolean; onClose: () => void; reps: Rep[] }) {
  const [text, setText] = useState('');
  const [override, setOverride] = useState<Partial<Record<Field, number | null>>>({});
  const importList = useImportList(bucketId);

  const parsed = useMemo(() => parsePasted(text), [text]);
  const mapping = useMemo(() => {
    const guessed = guessMapping(parsed.headers);
    const m: Partial<Record<Field, number>> = {};
    for (const f of FIELDS) {
      const o = override[f];
      if (o === null) continue;
      const v = o ?? guessed[f];
      if (v !== undefined) m[f] = v;
    }
    return m;
  }, [parsed.headers, override]);

  const built = useMemo(() => {
    const cell = (row: string[], f: Field) => (mapping[f] === undefined ? '' : row[mapping[f]!] ?? '');
    const rows: ImportRow[] = [];
    let noCompany = 0;
    let companyOnly = 0;
    let unknownRep = 0;
    for (const row of parsed.rows) {
      const companyName = cell(row, 'company');
      const fullName = cell(row, 'fullName') || `${cell(row, 'firstName')} ${cell(row, 'lastName')}`.trim();
      if (!companyName) { noCompany += 1; continue; }
      if (!fullName) companyOnly += 1;
      const repValue = cell(row, 'rep');
      const assigneeEmail = repValue ? repEmailFor(repValue, reps) : null;
      if (repValue && !assigneeEmail) unknownRep += 1;
      rows.push({
        companyName,
        companyDomain: cell(row, 'domain') || null,
        fullName: fullName || null,
        jobTitle: cell(row, 'jobTitle') || null,
        linkedinUrl: cell(row, 'linkedin') || null,
        email: cell(row, 'email') || null,
        country: cell(row, 'personCountry') || null,
        companyCountry: cell(row, 'companyCountry') || null,
        assigneeEmail,
      });
    }
    const companies = new Set(rows.map((r) => (r.companyDomain ?? r.companyName).toLowerCase())).size;
    const people = rows.filter((r) => r.fullName).length;
    const noIdentifier = rows.filter((r) => r.fullName && !r.linkedinUrl && !r.email).length;
    return { rows, companies, people, noCompany, companyOnly, unknownRep, noIdentifier };
  }, [parsed.rows, mapping, reps]);

  const canImport = built.rows.length > 0 && mapping.company !== undefined;
  const result = importList.data;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Import a list" description="Copy the rows from your sheet, header included, and paste them here. One row per person with the company on it, or one row per company for now. Nobody needs a rep yet: you split the list on the bucket page." size="xl">
      <div className="space-y-4">
        <textarea
          className={`${inputClass} h-40 font-mono text-body-xs`}
          placeholder={'Company\tWebsite\tFull name\tJob title\tLinkedIn\tEmail\nSodexo\tsodexo.com\tJane Doe\tHead of Bids\thttps://linkedin.com/in/janedoe\tjane@sodexo.com'}
          value={text}
          onChange={(e) => { setText(e.target.value); setOverride({}); importList.reset(); }}
        />

        {parsed.headers.length > 0 && (
          <div className="space-y-2">
            <p className="text-body-xs font-medium text-text-secondary">Columns recognised. Correct any that are wrong.</p>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              {FIELDS.map((f) => (
                <label key={f} className="flex items-center gap-2 text-body-xs text-text-secondary">
                  <span className="w-24 shrink-0">{FIELD_LABEL[f]}</span>
                  <select
                    className={inputClass}
                    value={mapping[f] ?? ''}
                    onChange={(e) => setOverride((o) => ({ ...o, [f]: e.target.value === '' ? null : Number(e.target.value) }))}
                  >
                    <option value="">Not mapped</option>
                    {parsed.headers.map((h, i) => <option key={i} value={i}>{h || `column ${i + 1}`}</option>)}
                  </select>
                </label>
              ))}
            </div>
            <ul className="text-body-xs text-text-tertiary">
              <li>{built.companies} companies and {built.people} people ready to import.</li>
              {built.companyOnly > 0 && <li>{built.companyOnly} rows carry only a company; the people can be pasted later.</li>}
              {built.noIdentifier > 0 && <li className="text-warning-dark">{built.noIdentifier} people without LinkedIn or email will be skipped: lemlist could never be matched to them.</li>}
              {built.noCompany > 0 && <li className="text-warning-dark">{built.noCompany} rows without a company, skipped.</li>}
              {built.unknownRep > 0 && <li className="text-warning-dark">{built.unknownRep} rows name a rep that is not a lemlist user; they come in unassigned.</li>}
            </ul>
          </div>
        )}

        {importList.error && <Alert variant="error">Import failed, nothing was changed: {importList.error.message}</Alert>}
        {result && (
          <Alert variant={result.alreadyInPilot.length + result.noIdentifier.length + result.customers.length > 0 ? 'warning' : 'success'}>
            Added {result.added} people. {result.companiesCreated} new {result.companiesCreated === 1 ? 'company' : 'companies'}, {result.companiesMatched} already in the bucket.
            {result.alreadyInPilot.length > 0 && ` Already there: ${result.alreadyInPilot.slice(0, 15).join(', ')}${result.alreadyInPilot.length > 15 ? ` and ${result.alreadyInPilot.length - 15} more` : ''}.`}
            {result.customers.length > 0 && ` Left out, existing customers: ${result.customers.join(', ')}.`}
            {result.noIdentifier.length > 0 && ` Skipped, no LinkedIn or email: ${result.noIdentifier.slice(0, 15).join(', ')}${result.noIdentifier.length > 15 ? ` and ${result.noIdentifier.length - 15} more` : ''}.`}
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
          {!result && <Button onClick={() => importList.mutate(built.rows)} isLoading={importList.isPending} disabled={!canImport}>Import {built.rows.length}</Button>}
        </div>
      </div>
    </Modal>
  );
}
